import { Apii } from '@/services/api.js';
import { useAppStore } from '@/store/app.js';
import { handleError } from '@/utils/error-handler';
import { notify } from 'notiwind';

export function useCardSubmission() {
    const ApiService = new Apii();

    async function addCard({
        manualCard,
        manualChosenCard,
        welcomeBonus,
        tieredBonuses,
        accountUserForCard,
        walletId,
        addCardBtnAction,
        errors,
        onSuccess,
        onResetBonuses,
    }) {
        errors.value = [];
        addCardBtnAction.value = true;

        // Credit limit is optional - if empty, set to 0
        if (manualCard.creditLimit === '') {
            manualCard.creditLimit = 0;
        }

        if (
            manualCard.card_not_found &&
            manualCard.card_not_found_name === ''
        ) {
            errors.value.push({
                message: 'Please enter the card suggestion.',
            });
        }

        if (
            manualCard.card_not_found &&
            manualCard.card_not_found_bank === ''
        ) {
            errors.value.push({
                message: 'Please enter the Issuing Bank suggestion.',
            });
        }

        if (errors.value.length > 0) {
            addCardBtnAction.value = false;
            return;
        }

        // Set bonusReceived based on account type and welcome bonus configuration
        if (manualCard.accountType === 'authorized_user') {
            manualCard.bonusReceived = null;
        } else if (manualCard.accountType === 'cardholder') {
            if (welcomeBonus.value.type && welcomeBonus.value.type !== null) {
                manualCard.bonusReceived = welcomeBonus.value.received
                    ? 'Y'
                    : 'N';
            } else {
                manualCard.bonusReceived = 'NA';
            }
        }

        // Fix annual fee logic before sending to backend
        if (manualChosenCard.value) {
            if (manualCard.user_managed_annual_fee) {
                manualCard.annualFee =
                    manualCard.annualFee !== ''
                        ? parseFloat(manualCard.annualFee)
                        : null;
            } else {
                manualCard.annualFee = manualChosenCard.value.annual_fee;
            }
        }

        // Fix credit limit: send 0 instead of empty string for authorized users
        if (
            manualCard.accountType === 'authorized_user' &&
            manualCard.creditLimit === ''
        ) {
            manualCard.creditLimit = 0;
        }

        // Map welcome bonus into legacy manualCard fields for backend compatibility
        if (welcomeBonus.value && welcomeBonus.value.type) {
            manualCard.bonusAmount =
                welcomeBonus.value.bonus_amount || 0;
            manualCard.minSpendDate =
                welcomeBonus.value.minimum_spend_deadline || '';
            manualCard.dateReceived =
                welcomeBonus.value.date_received || '';
        }

        const tieredToSend =
            welcomeBonus.value && welcomeBonus.value.type === 'tiered'
                ? (tieredBonuses.value || []).filter((t) => t && t.type)
                : [];
        const welcomeBonusPayload =
            welcomeBonus.value && welcomeBonus.value.type
                ? welcomeBonus.value
                : null;

        const data = {
            user: accountUserForCard,
            manualCard: { ...manualCard },
            welcome_bonus: welcomeBonusPayload,
            tiered_bonuses: tieredToSend,
        };

        try {
            const response = await ApiService.apiCall(
                '/wallets/' + walletId + '/cards',
                'post',
                data
            );

            if (manualCard.card_not_found) {
                notify({
                    group: 'success',
                    title: 'Request Sent',
                    text: "Your Missing Card Request was successfully submitted. You'll hear from us soon.",
                    type: 'success',
                });
                if (import.meta.env.VITE_APP_ENV === 'production') {
                    woopra.track('card-added-my10x', {
                        datetime: new Date(),
                        cardName: 'Missing card request',
                        uuid: localStorage.getItem('uuid'),
                    });
                }
            } else {
                notify({
                    group: 'success',
                    title: 'Success',
                    text: 'Card added to Wallet!',
                    type: 'success',
                });
                if (import.meta.env.VITE_APP_ENV === 'production') {
                    woopra.track('card-added-my10x', {
                        datetime: new Date(),
                        cardName: manualChosenCard.value.title,
                        uuid: localStorage.getItem('uuid'),
                    });
                }
            }

            // HubSpot Event Tracking
            trackHubSpotEvent(manualCard, manualChosenCard.value, response);

            addCardBtnAction.value = false;
            onResetBonuses();
            onSuccess();
        } catch (response) {
            handleError(response, { showValidationErrors: false });
            addCardBtnAction.value = false;
            errors.value.push({
                message: response?.response?.data?.message,
            });
        }
    }

    async function submitMissingCardRequest({
        manualCard,
        fieldErrors,
        accountUserForCard,
        walletId,
        addCardBtnAction,
        errors,
        manualStep,
    }) {
        // Clear previous errors
        Object.keys(fieldErrors).forEach((key) => delete fieldErrors[key]);

        // Validate Issuing bank
        if (
            !manualCard.card_not_found_bank ||
            manualCard.card_not_found_bank.trim() === ''
        ) {
            fieldErrors.card_not_found_bank = 'Enter issuing bank';
        }

        // Validate Card name
        if (
            !manualCard.card_not_found_name ||
            manualCard.card_not_found_name.trim() === ''
        ) {
            fieldErrors.card_not_found_name = 'Enter card name';
        }

        // If there are validation errors, stop here
        if (Object.keys(fieldErrors).length > 0) {
            return;
        }

        try {
            // Set the card_not_found flag to true
            manualCard.card_not_found = true;

            // Call addCardForMissingRequest
            await addCardForMissingRequest({
                manualCard,
                accountUserForCard,
                walletId,
                addCardBtnAction,
                errors,
            });

            // Show success step
            manualStep.value = -1;

            // Show notiwind notification
            notify({
                group: 'success',
                title: 'Card request submitted',
                text: "We'll review your request and add the card as soon as possible.",
                type: 'success',
            });
        } catch (error) {
            console.error('Error submitting missing card request:', error);
            notify({
                group: 'error',
                title: 'Error',
                text:
                    error?.response?.data?.message ||
                    'Failed to submit card request. Please try again.',
                type: 'error',
            });
        }
    }

    async function addCardForMissingRequest({
        manualCard,
        accountUserForCard,
        walletId,
        addCardBtnAction,
        errors,
    }) {
        addCardBtnAction.value = true;
        errors.value = [];

        // Validation for missing card fields
        if (manualCard.card_not_found_name === '') {
            errors.value.push({
                message: 'Please enter the card name.',
            });
        }

        if (manualCard.card_not_found_bank === '') {
            errors.value.push({
                message: 'Please enter the issuing bank.',
            });
        }

        if (errors.value.length > 0) {
            addCardBtnAction.value = false;
            throw new Error('Validation failed');
        }

        // Build payload to match edit-card-modal implementation
        const manualPayload = {
            chosenCard: null,
            type: 'personal',
            card_not_found: true,
            card_not_found_name: manualCard.card_not_found_name,
            card_not_found_bank: manualCard.card_not_found_bank,
            last_four: '',
            dateOpened: '',
            accountType: '',
            bonusAmount: 0,
            minSpendDate: '',
            bonusReceived: '',
            dateReceived: '',
            cardStatus: 'open',
            dateClosed: '',
            creditLimit: 0,
            credit_limit: 0,
            annualFee: 0,
            annual_fee: 0,
            dateBonusEarned: '',
            currentRewardsBalance: '',
            institution_code: '',
            opened_long_ago: false,
            closed_long_ago: false,
            business_id: null,
        };

        const data = {
            user: Number(accountUserForCard),
            manualCard: manualPayload,
            manual_card: {
                ...manualPayload,
            },
            welcome_bonus: {
                type: null,
                minimum_spend_deadline: null,
                minimum_spend_amount: null,
                bonus_amount: null,
                bonus_units: '',
                received: null,
                date_received: null,
            },
            tiered_bonuses: [],
        };

        try {
            await ApiService.apiCall(
                '/wallets/' + walletId + '/cards',
                'post',
                data
            );
            addCardBtnAction.value = false;
        } catch (error) {
            addCardBtnAction.value = false;
            throw error;
        }
    }

    return {
        addCard,
        submitMissingCardRequest,
        addCardForMissingRequest,
    };
}

function trackHubSpotEvent(manualCard, chosenCard, response) {
    if (!window._hsq) return;

    const appStore = useAppStore();
    const loggedInUser = appStore.userSettings;
    let userFirstName = null;
    let userLastName = null;

    if (loggedInUser && loggedInUser.name) {
        const nameParts = loggedInUser.name.split(' ');
        userFirstName = nameParts[0];
        if (nameParts.length > 1) {
            userLastName = nameParts.slice(1).join(' ');
        }
    }

    let cardProductName = null;
    let cardIssuerName = null;
    let cardProductId = null;
    let cardAnnualFee = null;

    if (manualCard.card_not_found) {
        cardProductName = manualCard.card_not_found_name;
        cardIssuerName = manualCard.card_not_found_bank;
    } else if (chosenCard) {
        const selectedCard = chosenCard;
        cardProductName = selectedCard.name || selectedCard.title;
        cardProductId = selectedCard.id;
        if (selectedCard.issuing_bank) {
            cardIssuerName = selectedCard.issuing_bank.name;
        }
        cardAnnualFee = manualCard.annualFee;
    }

    const eventProperties = {
        email: loggedInUser ? loggedInUser.email : null,
        firstname: userFirstName,
        lastname: userLastName,
        my10x_user_uuid: loggedInUser ? loggedInUser.uuid : null,
        card_product_id: cardProductId,
        card_product_name: cardProductName,
        card_issuer: cardIssuerName,
        user_card_last_four: manualCard.last_four,
        user_card_opened_date: manualCard.dateOpened,
        user_card_credit_limit: manualCard.creditLimit,
        user_wallet_card_id:
            response.data?.card?.id || response.data?.id || null,
        is_business_card: manualCard.accountType
            ? manualCard.accountType.toLowerCase().includes('business')
            : null,
        card_annual_fee: cardAnnualFee,
        card_status_in_wallet: manualCard.cardStatus,
        card_rewards_balance: manualCard.currentRewardsBalance,
        card_bonus_received_flag: manualCard.bonusReceived,
        card_is_missing_from_system_request: manualCard.card_not_found,
        institution_code: manualCard.institution_code,
    };

    for (const key in eventProperties) {
        if (
            eventProperties[key] === null ||
            eventProperties[key] === undefined
        ) {
            delete eventProperties[key];
        }
    }

    window._hsq.push([
        'trackCustomBehavioralEvent',
        {
            name: 'pe22692776_add_card_to_wallet_manually',
            properties: eventProperties,
        },
    ]);
}
